import { DropTargetMonitor } from 'react-dnd';
import { isDroppable } from './isDroppable';
import { NodeModel, TreeState, DragItem } from '../types';

type CompareResult = 'up' | 'down';

type VerticalPosition = 'upper' | 'middle' | 'lower';

type DropTarget = {
    id: NodeModel['id'];
    index: number;
    name: string;
} | null;

type CompareYCoord = (el: Element, pointerY: number) => CompareResult;

type GetInnerIndex = (listItems: NodeListOf<Element>, monitor: DropTargetMonitor) => number;

type GetOuterIndex = (node: NodeModel, nodeEl: HTMLElement, monitor: DropTargetMonitor) => number | null;

const compareYCoord: CompareYCoord = (el, pointerY) => {
    const bbox = el.getBoundingClientRect();
    const centerY = bbox.top + bbox.height / 2;
    return pointerY > centerY ? 'down' : 'up';
};

const getInnerIndex: GetInnerIndex = (listItems, monitor) => {
    let pos = '';
    let index = 0;

    listItems.forEach((el, key) => {
        const flag = compareYCoord(el, monitor.getClientOffset()?.y || 0);

        if (pos === '') {
            pos = flag;
        } else if (pos !== flag) {
            pos = flag;
            index = key;
        }

        if (key === listItems.length - 1 && flag === 'down') {
            index = key + 1;
        }
    });

    return index;
};

const getOuterIndex: GetOuterIndex = (node, nodeEl, monitor) => {
    const parentList = nodeEl.closest('.dnd-container');
    const parentListItems = parentList?.querySelectorAll(':scope > .dnd-item');

    if (!parentListItems) {
        return null;
    }

    return getInnerIndex(parentListItems, monitor);
};

const getHoverPosition = <T>(el: Element, pointerY: number, context: TreeState<T>): VerticalPosition => {
    const bbox = el.getBoundingClientRect();
    const offsetY = context.dropTargetOffset;
    const upSideY = bbox.top + offsetY;
    const lowerSideY = bbox.bottom - offsetY;

    if (pointerY > lowerSideY) {
        return 'lower';
    } else if (pointerY < upSideY) {
        return 'upper';
    }

    return 'middle';
};

export const getDropTarget = <T>(
    node: NodeModel<T> | null,
    parentNode: any,
    nodeEl: HTMLElement | null,
    monitor: DropTargetMonitor,
    context: TreeState<T>
): DropTarget => {
    const labelKey = context?.labelKey ?? 'displayName';
    const buildNodeTarget = (n: any, index: number): DropTarget => ({
        id: n?.id,
        name: n?.[labelKey],
        index,
    });

    const getParentDropTarget = (): DropTarget => {
        const outerIndex = getOuterIndex(node as NodeModel<T>, nodeEl as HTMLElement, monitor);
        return outerIndex === null ? null : buildNodeTarget(parentNode, outerIndex);
    };

    if (!nodeEl) {
        return null;
    }

    if (node === null) {
        const listItems = nodeEl.querySelectorAll(':scope > .dnd-item');
        return buildNodeTarget(context.parentNode, getInnerIndex(listItems, monitor));
    }

    const dragSource: DragItem<T> = monitor.getItem();
    const list = nodeEl.querySelector('.dnd-container');
    const pointerY = monitor.getClientOffset()?.y || 0;
    const hoverPosition = getHoverPosition(nodeEl, pointerY, context);
    const canDropToParent = isDroppable(dragSource, parentNode?.id, context);

    if (!list) {
        if (hoverPosition === 'middle') {
            return buildNodeTarget(node, 0);
        }
        if (canDropToParent) {
            return getParentDropTarget();
        }
        return null;
    }

    if (hoverPosition === 'upper') {
        if (canDropToParent) {
            return getParentDropTarget();
        }
        return buildNodeTarget(node, 0);
    }

    const listItems = list.querySelectorAll(':scope > .dnd-item');
    return buildNodeTarget(node, getInnerIndex(listItems, monitor));
};
